import type { ReactNode } from 'react';
import type { Auth0ProviderOptions } from '@auth0/auth0-react';
import type { ShellBridge } from '@opengov/psp-contract';
import { StandaloneAuth0Provider } from './StandaloneAuth0Provider';
import { BridgedAuth0Provider } from './BridgedAuth0Provider';

/**
 * Standalone-mode props: a regular Auth0 SDK config. `shellBridge` is
 * forbidden via `?: never` so it cannot be passed by accident.
 */
type StandaloneProps = {
  mode: 'standalone';
  /** ⛔ Disallowed in standalone mode. */
  shellBridge?: never;
} & Auth0ProviderOptions;

/**
 * Shell-mode props: just `shellBridge` (forwarded straight to
 * `BridgedAuth0Provider`). Every `Auth0ProviderOptions` field is forbidden
 * via a mapped `?: never` type so the compiler keeps the two modes from
 * accidentally mixing — and the rule stays in sync with the upstream SDK
 * even if Auth0 adds new options.
 */
type ShellProps = {
  mode: 'shell';
  shellBridge: ShellBridge;
  children: ReactNode;
} & {
  /** ⛔ Every Auth0ProviderOptions field is forbidden in shell mode. */
  [K in keyof Omit<Auth0ProviderOptions, 'children'>]?: never;
};

export type ShellAuth0ProviderProps = StandaloneProps | ShellProps;

/**
 * Mode-aware Auth0 provider for PSP shell-mounted microfrontends.
 *
 *   - `mode="standalone"`: thin-wraps `<Auth0Provider>` from
 *     `@auth0/auth0-react`. Adopt for local dev entries and for running suite
 *     apps outside PSP. Accepts the full `Auth0ProviderOptions` surface.
 *   - `mode="shell"`: reads through `shellBridge.auth0` (populated by the PSP
 *     shell via `ModuleMountOptions.shell.auth0`). Use inside the module's
 *     `mount(container, options)` body, passing `options.shell` as
 *     `shellBridge`.
 *
 * The `mode` prop is required to make the caller's intent explicit at the
 * call site. Each branch makes the other branch's props a compile error.
 */
export function ShellAuth0Provider(props: ShellAuth0ProviderProps) {
  if (props.mode === 'shell') {
    return (
      <BridgedAuth0Provider shellBridge={props.shellBridge}>{props.children}</BridgedAuth0Provider>
    );
  }
  // Standalone branch: strip our two private props before forwarding to the
  // real Auth0 provider. `shellBridge` is typed `never` here so at runtime it's
  // undefined; spreading it is a no-op on the Auth0 side.
  const { mode, shellBridge, ...auth0Props } = props;
  void mode;
  void shellBridge;
  return <StandaloneAuth0Provider {...auth0Props} />;
}
